import { useState } from "react";
import { toast } from "sonner";
import { DeleteConfirmationDialog } from "@/components/invoice/DeleteConfirmationDialog";
import { ScheduleHeader } from "@/components/invoice/ScheduleHeader";
import { AddInvoiceDialog } from "@/components/invoice/header/AddInvoiceDialog";
import { supabase } from "@/integrations/supabase/client";
import { useInvoiceData } from "@/hooks/useInvoiceData";
import { InvoiceGridView } from "@/components/invoice/views/InvoiceGridView";
import { InvoiceListView } from "@/components/invoice/views/InvoiceListView";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CreateInvoiceForm } from "@/components/invoice/CreateInvoiceForm";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "@/components/ui/scroll-area";
import { showToast } from "@/lib/toast";
import { ChecklistOnboarding } from "@/components/onboarding/ChecklistOnboarding";
import { ClipboardList, Clock, CreditCard, DollarSign, BarChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

type FilterType = "all" | "scheduled" | "unscheduled" | "paid" | "unpaid" | "recipient";
type BulkAction = "delete" | "schedule" | null;
type ViewType = "grid" | "list";

// Summary stats component to display invoice statistics
interface Invoice {
  id: string;
  status: string;
  subtotal: number;
  total_tax_amount: number | null;
  currency?: string;
}

interface InvoiceSummaryStatsProps {
  invoices: Invoice[];
  allInvoices: Invoice[];
}

const InvoiceSummaryStats = ({ invoices, allInvoices }: InvoiceSummaryStatsProps) => {
  const { t } = useTranslation();

  // Calculate stats for shown invoices (based on filter)
  const totalShownInvoices = invoices.length;

  // Calculate global stats across all invoices
  const totalScheduledInvoices = allInvoices.filter(inv => inv.status === "scheduled").length;
  const totalUnpaidInvoices = allInvoices.filter(inv => inv.status === "unpaid").length;

  // Group invoice amounts by currency for filtered invoices (only shown invoices)
  const invoiceValuesByCurrency = invoices.reduce((acc, inv) => {
    const currency = inv.currency || 'EUR'; // Default to EUR if currency is not defined
    const amount = inv.subtotal + (inv.total_tax_amount || 0);

    if (!acc[currency]) {
      acc[currency] = 0;
    }

    acc[currency] += amount;
    return acc;
  }, {} as Record<string, number>);

  // Get currency display settings
  const getCurrencySymbol = (currency: string): string => {
    switch (currency) {
      case 'USD': return '$';
      case 'GBP': return '£';
      case 'EUR': return '€';
      default: return currency;
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Card className="p-4 bg-white border border-[#E2E8F0] rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <div className="bg-[#F1F0FB] p-2 rounded-lg">
            <ClipboardList className="h-5 w-5 text-[#34375B]" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-[#636782] text-left">{t("dashboard.shown_invoices", "Shown Invoices")}</span>
            <span className="text-2xl font-bold text-[#34375B] text-left">{totalShownInvoices}</span>
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-white border border-[#E2E8F0] rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <div className="bg-[#ECF0FF] p-2 rounded-lg">
            <Clock className="h-5 w-5 text-[#8791F3]" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-[#636782] text-left">{t("dashboard.scheduled_invoices", "Scheduled")}</span>
            <span className="text-2xl font-bold text-[#8791F3] text-left">{totalScheduledInvoices}</span>
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-white border border-[#E2E8F0] rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <div className="bg-[#FFF0F0] p-2 rounded-lg">
            <CreditCard className="h-5 w-5 text-[#F39D96]" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-[#636782] text-left">{t("dashboard.unpaid_invoices", "Unpaid")}</span>
            <span className="text-2xl font-bold text-[#F39D96] text-left">{totalUnpaidInvoices}</span>
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-white border border-[#E2E8F0] rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <div className="bg-[#E6F7F5] p-2 rounded-lg">
            <BarChart className="h-5 w-5 text-[#5CC2BB]" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-[#636782] text-left">{t("dashboard.total_value", "Total Value")}</span>
            <div className="flex flex-col">
              {Object.entries(invoiceValuesByCurrency).map(([currency, amount]) => (
                <span key={currency} className="text-xl font-bold text-[#5CC2BB] text-left">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(amount)}
                </span>
              ))}
              {Object.keys(invoiceValuesByCurrency).length === 0 && (
                <span className="text-xl font-bold text-[#5CC2BB] text-left">€0.00</span>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

const ScheduleInvoice = () => {
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [filter, setFilter] = useState<FilterType>("all");
  const [recipientNameFilter, setRecipientNameFilter] = useState("");
  const [selectedBulkAction, setSelectedBulkAction] = useState<BulkAction>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [viewType, setViewType] = useState<ViewType>("grid");
  const { t } = useTranslation();

  const { data: invoicesData, isLoading, refetch } = useInvoiceData();

  const filteredInvoices = invoicesData?.filter(invoice => {
    const mainFilterPass = filter === "all" ? true :
      filter === "recipient" ? true :
        filter === "scheduled" ? invoice.status === "scheduled" :
          filter === "unscheduled" ? invoice.status === "unscheduled" :
            filter === "paid" ? invoice.status === "paid" :
              filter === "unpaid" ? invoice.status === "unpaid" :
                true;

    const recipientFilterPass = filter === "recipient" && recipientNameFilter
      ? invoice.recipient_name.toLowerCase().includes(recipientNameFilter.toLowerCase())
      : true;

    return mainFilterPass && recipientFilterPass;
  });

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
    if (newFilter !== "recipient") {
      setRecipientNameFilter("");
    }
  };

  const handleSelectAll = () => {
    if (!filteredInvoices) return;

    if (selectedInvoices.length === filteredInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredInvoices.map(invoice => invoice.id));
    }
  };

  const handleBulkActionSelect = async (action: BulkAction) => {
    if (!action || selectedInvoices.length === 0) return;

    if (action === "delete") {
      setShowDeleteDialog(true);
    } else if (action === "schedule") {
      setIsScheduleOpen(true);
    }
    setSelectedBulkAction(null);
  };

  const handleDeleteInvoices = async () => {
    try {
      if (!selectedInvoices || selectedInvoices.length === 0) {
        showToast.error(t("toasts.no_invoices_selected_delete"));
        return;
      }

      const { data: schedules, error: scheduleQueryError } = await supabase
        .from('invoice_schedules')
        .select('id')
        .in('invoice_id', selectedInvoices);

      if (scheduleQueryError) throw scheduleQueryError;

      if (schedules && schedules.length > 0) {
        const scheduleIds = schedules.map(s => s.id);

        const { error: remindersError } = await supabase
          .from('invoice_schedule_reminders')
          .delete()
          .in('invoice_schedule_id', scheduleIds);

        if (remindersError) throw remindersError;

        const { error: schedulesError } = await supabase
          .from('invoice_schedules')
          .delete()
          .in('invoice_id', selectedInvoices);

        if (schedulesError) throw schedulesError;
      }

      const { error: invoicesError } = await supabase
        .from('invoices')
        .delete()
        .in('id', selectedInvoices);

      if (invoicesError) throw invoicesError;

      showToast.success(t("toasts.delete_successful"));
      setSelectedInvoices([]);
      setShowDeleteDialog(false);
      refetch();
    } catch (error) {
      console.error('Error deleting invoices:', error);
      showToast.error(t("toasts.delete_failed"));
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="flex-1 w-full">
        <div className="flex flex-col space-y-4 md:space-y-6 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <h1 className="font-archivoblack text-2xl md:text-3xl lg:text-5xl font-bold text-[#34375B] break-words">{t("dashboard.invoices")}</h1>
            </div>
            <div className="w-full sm:w-auto">
              <AddInvoiceDialog />
            </div>
          </div>

          {/* Add summary stats before filters */}
          <InvoiceSummaryStats invoices={filteredInvoices || []} allInvoices={invoicesData || []} />

          <ScheduleHeader
            selectedInvoices={selectedInvoices}
            onFilterChange={handleFilterChange}
            onBulkActionSelect={handleBulkActionSelect}
            onDeleteClick={() => setShowDeleteDialog(true)}
            currentFilter={filter}
            onSelectAll={handleSelectAll}
            hasVisibleInvoices={Boolean(filteredInvoices?.length)}
            viewType={viewType}
            onViewTypeChange={setViewType}
          />

          {filter === "recipient" && (
            <div className="w-full sm:max-w-sm">
              <Input
                type="text"
                placeholder={t("dashboard.filter_recipient_placeholder", "Filter by recipient name...")}
                value={recipientNameFilter}
                onChange={(e) => setRecipientNameFilter(e.target.value)}
                className="w-full"
              />
            </div>
          )}

          <div className="w-full">
            {viewType === "grid" ? (
              <InvoiceGridView
                invoices={filteredInvoices || []}
                selectedInvoices={selectedInvoices}
                onSelect={(id) => {
                  setSelectedInvoices(prev =>
                    prev.includes(id)
                      ? prev.filter(i => i !== id)
                      : [...prev, id]
                  );
                }}
                onCheckboxChange={(checked, id) => {
                  if (id === "all") {
                    if (checked && filteredInvoices) {
                      setSelectedInvoices(filteredInvoices.map(inv => inv.id));
                    } else {
                      setSelectedInvoices([]);
                    }
                  } else {
                    setSelectedInvoices(prev =>
                      checked
                        ? [...prev, id]
                        : prev.filter(i => i !== id)
                    );
                  }
                }}
                filter={filter}
              />
            ) : (
              <InvoiceListView
                invoices={filteredInvoices || []}
                selectedInvoices={selectedInvoices}
                onSelect={(id) => {
                  setSelectedInvoices(prev =>
                    prev.includes(id)
                      ? prev.filter(i => i !== id)
                      : [...prev, id]
                  );
                }}
                onCheckboxChange={(checked, id) => {
                  if (id === "all") {
                    if (checked && filteredInvoices) {
                      setSelectedInvoices(filteredInvoices.map(inv => inv.id));
                    } else {
                      setSelectedInvoices([]);
                    }
                  } else {
                    setSelectedInvoices(prev =>
                      checked
                        ? [...prev, id]
                        : prev.filter(i => i !== id)
                    );
                  }
                }}
                filter={filter}
              />
            )}
          </div>
        </div>
      </div>

      <Dialog open={isScheduleOpen} onOpenChange={setIsScheduleOpen}>
        <DialogContent className="max-w-3xl">
          <ScrollArea className="max-h-[80vh]">
            <CreateInvoiceForm
              mode="schedule"
              invoiceIds={selectedInvoices}
              onSuccess={() => {
                setIsScheduleOpen(false);
                refetch();
              }}
              onCancel={() => setIsScheduleOpen(false)}
              initialScheduleExpanded={true}
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteInvoices}
        selectedCount={selectedInvoices.length}
      />

      <ChecklistOnboarding />
    </>
  );
};

export default ScheduleInvoice;
